import { useCallback } from 'react';
import { useModalHistory } from 'context/ModalRouter';
import { ApiClientError } from 'api/client';
import { modalRoutes } from 'constants/routes';

export type SeatLimitContext = 'invite' | 'restore';

type SeatLimit = { current: number; limit: number };

export type SeatLimitModalState = {
  seatLimitContext: SeatLimitContext;
  seatLimit?: SeatLimit;
};

type SeatErrorData =
  | { code: 'USER_LIMIT_REACHED'; current?: unknown; limit?: unknown }
  | { code: 'SUBSCRIPTION_EXPIRED' };

type SeatErrorResponseBody = {
  error_data?: SeatErrorData;
};

function readSeatErrorData(error: ApiClientError): SeatErrorData | undefined {
  const response = error.response as
    | { data?: SeatErrorResponseBody }
    | undefined;
  return response?.data?.error_data;
}

function readSeatLimit({
  current,
  limit,
}: {
  current?: unknown;
  limit?: unknown;
}): SeatLimit | undefined {
  if (typeof current === 'number' && typeof limit === 'number') {
    return { current, limit };
  }

  return undefined;
}

export function useOpenSeatLimitModal() {
  const modalHistory = useModalHistory();

  return useCallback(
    (context: SeatLimitContext, seatLimit?: SeatLimit): void => {
      const state: SeatLimitModalState = seatLimit
        ? { seatLimitContext: context, seatLimit }
        : { seatLimitContext: context };

      modalHistory.push({ hash: modalRoutes.USER_LIMIT_REACHED, state });
    },
    [modalHistory],
  );
}

export function useSeatLimitErrorHandler(context: SeatLimitContext = 'invite') {
  const modalHistory = useModalHistory();
  const openSeatLimitModal = useOpenSeatLimitModal();

  return useCallback(
    (error: ApiClientError): boolean => {
      const errorData = readSeatErrorData(error);

      if (!errorData) {
        return false;
      }

      if (errorData.code === 'USER_LIMIT_REACHED') {
        openSeatLimitModal(context, readSeatLimit(errorData));
        return true;
      }

      if (errorData.code === 'SUBSCRIPTION_EXPIRED') {
        modalHistory.push({ hash: modalRoutes.SUBSCRIPTION });
        return true;
      }

      return false;
    },
    [modalHistory, openSeatLimitModal, context],
  );
}
